/**
 * Justified rows: every image keeps its full frame (no cropping) and each
 * row is scaled to exactly fill the container width. A row closes at
 * whichever break point (before or after the next image) lands closer to
 * the target height, which keeps rows even. The final row is left short
 * (not stretched) only when filling it would make it much too tall.
 */
export function justifyRows(items, containerWidth, { targetHeight, gap }) {
  const rows = [];
  if (!containerWidth || !items.length) return rows;

  const heightFor = (aspectSum, count) => (containerWidth - gap * (count - 1)) / aspectSum;

  let row = [];
  let aspectSum = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const nextSum = aspectSum + item.aspect;
    const nextHeight = heightFor(nextSum, row.length + 1);

    if (nextHeight > targetHeight) {
      row.push(item);
      aspectSum = nextSum;
      continue;
    }

    // Adding this item drops the row below target. Close before or after it,
    // whichever lands nearer the target.
    if (row.length && Math.abs(heightFor(aspectSum, row.length) - targetHeight) < Math.abs(nextHeight - targetHeight)) {
      rows.push(makeRow(row, heightFor(aspectSum, row.length)));
      row = [item];
      aspectSum = item.aspect;
    } else {
      row.push(item);
      rows.push(makeRow(row, nextHeight));
      row = [];
      aspectSum = 0;
    }
  }

  if (row.length) {
    // Fill the width too if that doesn't make the row much taller than the rest.
    const fitted = heightFor(aspectSum, row.length);
    rows.push(fitted <= targetHeight * 1.35 ? makeRow(row, fitted) : makeRow(row, targetHeight, true));
  }
  return rows;
}

const makeRow = (items, height, last = false) => {
  const aspectSum = items.reduce((s, it) => s + it.aspect, 0);
  return {
    items,
    height,
    last,
    widths: items.map((it) => it.aspect * height),
    // flex-grow shares summing to 1: flexbox only hands out a fraction of
    // the free space when grow values total less than 1 (a lone portrait).
    shares: items.map((it) => it.aspect / aspectSum),
  };
};

/** Target row height that gives two portraits side by side on phones. */
export function gridMetrics(width) {
  if (width < 600) return { targetHeight: Math.max(200, width * 0.74), gap: 4 };
  if (width < 1024) return { targetHeight: width * 0.4, gap: 8 };
  return { targetHeight: Math.min(460, Math.max(300, width * 0.28)), gap: 12 };
}

/**
 * Pick the photos for a banner (home slides, gallery heroes, About band),
 * respecting the pool's (already shuffled) order.
 *
 * Banners only ever use vertical photos: one full-bleed on phones, three
 * side by side on normal wide screens (two when the frame is nearly square,
 * four when it's ultra-wide). A landscape is used only if the gallery has
 * no vertical photos at all.
 */
export function fillFrame(pool, frameAspect) {
  if (!pool.length) return [];
  const portraits = pool.filter((i) => i.aspect < 0.95);
  if (!portraits.length) return [pool[0]];

  let count = 3;
  if (frameAspect < 1.05) count = 1;
  else if (frameAspect < 1.2) count = 2;
  else if (frameAspect > 2.6) count = 4;
  return portraits.slice(0, count);
}
